import React, { useEffect, useRef, useState } from 'react';
import { Table } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import './index.less';
import classNames from 'classnames';
import { ICellDataType, ICorrectTable } from '@/types';

interface DataSourceItem {
    key: any;
    [prop: string]: ICellDataType;
}

interface IProps {
    data: ICorrectTable;
}

const sheetTableCellClassNames = (record: ICellDataType, firstRowMergedRows: number) => ({
    center: record.v.ht === '0',
    right: record.v.ht === '2',
    nowrap: record.r === 0 || (record.cellType === 2 && [0, 2].includes(record.status)), // 第一行 || 2:项目分类
    blank: record.cellType === 0 && !record.v.v && record.v.v !== 0,
    head: record.r <= firstRowMergedRows,
});

const SheetTable: React.FC<IProps> = props => {
    const [columns, setColumns] = useState<ColumnsType<DataSourceItem>>([]);
    const [dataSource, setDataSource] = useState<DataSourceItem[]>([]);
    const firstRowMergedRows = useRef<number>(0);

    const renderCell = (record: ICellDataType) => {
        if (!record) return '';
        // 无需批改
        if (record.status === 0) {
            return (
                <div className={classNames('sheet-table-cell', sheetTableCellClassNames(record, firstRowMergedRows.current))}>
                    <span className="cell-content">{record.v.v}</span>
                </div>
            );
        }
        // 批改失败
        if (record.status === 3) {
            return (
                <div className={classNames('sheet-table-cell', sheetTableCellClassNames(record, firstRowMergedRows.current))}>
                    <span className="cell-content">{record.v.v}</span>
                    <span className="cell-score red">批改失败</span>
                </div>
            );
        }
        // 1 和 2
        return (
            <div className={classNames('sheet-table-cell', sheetTableCellClassNames(record, firstRowMergedRows.current))}>
                <span className="cell-content">
                    {record.correct?.correctPointList.map((item, index: number) => (
                        <span
                            className={classNames('cell-content-text', {
                                red: item.showStatus === 0,
                                green: item.showStatus === 1,
                            })}
                            key={`${index + 1}`}
                        >
                            {item.answer}
                            {record.cellType === 2 && <br />}
                        </span>
                    ))}
                    {!record.correct && record.v.v}
                </span>
                {record.correct && record.correct !== null && record.cellType !== 0 && (
                    <span
                        className={classNames('cell-score', {
                            red: record.correct.score === 0,
                            green: record.correct.score > 0,
                        })}
                    >
                        {record.r === 0 && record.c === 0 ? '过程：' : ''}
                        {record.correct.score}分
                    </span>
                )}
            </div>
        );
    };

    useEffect(() => {
        const tableData = props.data;

        const newColumns: ColumnsType<DataSourceItem> = [];
        /**
         * 必须使用new Array 强制展示有多少行数据
         */
        let newDataSource: DataSourceItem[] = new Array(tableData.row);

        for (let index = 0; index < tableData.cellData.length; index++) {
            const element = tableData.cellData[index];
            // 首行数据
            if (element.r === 0 && element.v.mc && element.v.mc.cs && element.v.mc.cs > 1) {
                firstRowMergedRows.current = Math.max(firstRowMergedRows.current, element.v.mc.cs - 1);
            }
            newDataSource[element.r] = Object.assign(newDataSource[element.r] || {}, {
                ['col_' + element.c]: element,
                key: element.r,
            });
        }

        newDataSource = Array.from(newDataSource).map((item, index: any) => {
            // 补全空位
            if (!item) {
                return {
                    col_0: {
                        c: 0,
                        cellType: null,
                        correct: null,
                        r: index,
                        status: 0,
                        v: { v: null },
                    },
                    key: index,
                } satisfies DataSourceItem;
            }
            return item;
        });

        for (let index = 0; index < tableData.col; index++) {
            newColumns.push({
                title: index,
                key: index,
                dataIndex: 'col_' + index,
                render: (record: ICellDataType) => renderCell(record),
                onCell: (record: DataSourceItem) => {
                    if (!record['col_' + index]) return {};

                    const width = record['col_' + index].cellType === 3 ? '54%' : undefined;

                    if (record['col_' + index].v.mc?.rs && record['col_' + index].v.mc?.cs) {
                        return {
                            colSpan: record['col_' + index].v.mc!.cs,
                            rowSpan: record['col_' + index].v.mc!.rs,
                            width,
                        };
                    }
                    if (record['col_' + index].v.mc) {
                        return {
                            colSpan: 0,
                            rowSpan: 0,
                            width,
                        };
                    }
                    return { width };
                },
            });
        }
        setColumns(newColumns);
        setDataSource(newDataSource);
    }, [props.data]);

    return (
        <Table
            bordered
            className="sheet-table"
            columns={columns}
            dataSource={dataSource}
            pagination={false}
            rowKey={'key'}
            scroll={{ x: 1070 }}
            showHeader={false}
        />
    );
};

export default SheetTable;
